import BaseButton from "@/components/common/baseButton";
import { useTranslation } from "react-i18next";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import BaseInput from "@/components/common/baseInput";
import "./formsCreateFunDay.css";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BaseDateInput from "@/components/common/baseDateInput";
import NumericFormatCustom from "@/components/common/numericFormatCustom";
import { Checkbox, FormControlLabel, MenuItem, Radio, RadioGroup } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { formatDate } from "@/util";
import { useEffect, useState } from "react";
import { CreateFunDayRequest } from "@/models/forms";
import { MealTypes, NatureFunDayTypes, VisitorsTypes } from "@/models/enums";
import { Region } from "@/models/apiModels";
import api from "@/services/api";
import dayjs from "dayjs";
import popupService from "@/services/popupService";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const FormsCreateFunDay = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [regions, setRegions] = useState<Region[]>([]);
    const [requestedFrom, setRequestedFrom] = useState<Date | null>(null);
    const [requestedTo, setRequestedTo] = useState<Date | null>(null);
    const [isCheckedChallenges, setIsCheckedChallenges] = useState(false);
    const [isCheckedTrip, setIsCheckedTrip] = useState(false);
    const [isCheckedCombined, setIsCheckedCombined] = useState(false);
    const [isCheckedRefreshmentsArrival, setIsCheckedRefreshmentsArrival] = useState(false);
    const [isCheckedFullBreakfast, setIsCheckedFullBreakfast] = useState(false);
    const [isCheckedRefreshmentsWay, setIsCheckedRefreshmentsWay] = useState(false);
    const [isCheckedLunch, setIsCheckedLunch] = useState(false);
    const [isCheckedDinner, setIsCheckedDinner] = useState(false);
    const [createFunDayRequest, setCreateFunDayRequest] = useState<CreateFunDayRequest>({
        clientName: "",
        mobile: "",
        email: "",
        goalOfFunDay: "",
        numberOfParticipants: null,
        desiredRegionOneId: null,
        desiredRegionTwoId: null,
        desiredRegionThreeId: null,
        budgetPerEmployee: "",
        familyBudget: "",
        budgetForCouple: "",
        budgetComments: "",
        requestedFrom: null,
        requestedTo: null,
        alternativeHolidayDateOne: null,
        alternativeHolidayDateTwo: null,
        alternativeHolidayDateThree: null,
        alternativeHolidayDateComments: "",
        visitors: VisitorsTypes.EmployeesOnly,
        needBus: true,
        needGuide: true,
        natureFunDayType: null,
        natureFunDayComments: "",
        wantIncorporateLectureRecognition: true,
        incorporateLectureRecognitionBudget: "",
        incorporateLectureRecognitionComments: "",
        mealType: null,
        mealComments: "",
    });
    const currentDate = new Date();

    async function getCreateFunDayInitData() {
        const regionsListDataResponsePromise = api.getRegions();
        setRegions((await regionsListDataResponsePromise).data);
    }

    const handleCheckedChallenges = () => {
        setIsCheckedChallenges((prevChecked) => !prevChecked);
        handleChangeNatureFunDayType(!isCheckedChallenges, NatureFunDayTypes[NatureFunDayTypes.Challenges] + ",");
    };

    const handleCheckedTrip = () => {
        setIsCheckedTrip((prevChecked) => !prevChecked);
        handleChangeNatureFunDayType(!isCheckedTrip, NatureFunDayTypes[NatureFunDayTypes.Trip] + ",");
    };

    const handleCheckedCombined = () => {
        setIsCheckedCombined((prevChecked) => !prevChecked);
        handleChangeNatureFunDayType(!isCheckedCombined, NatureFunDayTypes[NatureFunDayTypes.Combined] + ",");
    };

    const handleCheckedRefreshmentsOnArrival = () => {
        setIsCheckedRefreshmentsArrival((prevChecked) => !prevChecked);
        handleChangeMealType(!isCheckedRefreshmentsArrival, MealTypes[MealTypes.RefreshmentsOnArrival] + ",");
    };

    const handleCheckedFullBreakfast = () => {
        setIsCheckedFullBreakfast((prevChecked) => !prevChecked);
        handleChangeMealType(!isCheckedFullBreakfast, MealTypes[MealTypes.FullBreakfast] + ",");
    };

    const handleCheckedRefreshmentsWay = () => {
        setIsCheckedRefreshmentsWay((prevChecked) => !prevChecked);
        handleChangeMealType(!isCheckedRefreshmentsWay, MealTypes[MealTypes.RefreshmentsOnTheWay] + ",");
    };

    const handleCheckedLunch = () => {
        setIsCheckedLunch((prevChecked) => !prevChecked);
        handleChangeMealType(!isCheckedLunch, MealTypes[MealTypes.Lunch] + ",");
    };

    const handleCheckedDinner = () => {
        setIsCheckedDinner((prevChecked) => !prevChecked);
        handleChangeMealType(!isCheckedDinner, MealTypes[MealTypes.Dinner] + ",");
    };

    function handleChangeMealType(isChange: boolean, enumType: string) {
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            mealType: isChange ? (prevData.mealType ?? "") + enumType : prevData.mealType?.replace(enumType, "") ?? "",
        }));
    }

    function handleChangeNatureFunDayType(isChange: boolean, enumType: string) {
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            natureFunDayType: isChange
                ? (prevData.natureFunDayType ?? "") + enumType
                : prevData.natureFunDayType?.replace(enumType, "") ?? "",
        }));
    }

    function handleOnChangeRequestedFrom(e: any) {
        setRequestedFrom(e);
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("HH:mm:ss") : "";
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            requestedFrom: e ? formattedDate : null,
        }));
    }

    function handleOnChangeRequestedTo(e: any) {
        setRequestedTo(e);
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("HH:mm:ss") : "";
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            requestedTo: e ? formattedDate : null,
        }));
    }

    function handleOnChangeDateOne(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            alternativeHolidayDateOne: e ? formattedDate : null,
        }));
    }

    function handleOnChangeDateTwo(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            alternativeHolidayDateTwo: e ? formattedDate : null,
        }));
    }

    function handleOnChangeDateThree(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setCreateFunDayRequest((prevData) => ({
            ...prevData,
            alternativeHolidayDateThree: e ? formattedDate : null,
        }));
    }

    const submitOnClick = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        popupService.showLoading();
        if (
            !createFunDayRequest.alternativeHolidayDateOne ||
            !createFunDayRequest.alternativeHolidayDateTwo ||
            createFunDayRequest.alternativeHolidayDateThree === ""
        ) {
            popupService.showError(t("error.InvalidDate"));
            return;
        }
        try {
            const request: CreateFunDayRequest = {
                clientName: createFunDayRequest.clientName,
                mobile: createFunDayRequest.mobile,
                email: createFunDayRequest.email,
                alternativeHolidayDateOne: createFunDayRequest.alternativeHolidayDateOne,
                alternativeHolidayDateTwo: createFunDayRequest.alternativeHolidayDateTwo,
                alternativeHolidayDateThree: createFunDayRequest.alternativeHolidayDateThree,
                alternativeHolidayDateComments: createFunDayRequest.alternativeHolidayDateComments,
                budgetPerEmployee: createFunDayRequest.budgetPerEmployee,
                budgetForCouple: createFunDayRequest.budgetForCouple,
                familyBudget: createFunDayRequest.familyBudget,
                budgetComments: createFunDayRequest.budgetComments,
                desiredRegionOneId: createFunDayRequest.desiredRegionOneId,
                desiredRegionTwoId: createFunDayRequest.desiredRegionTwoId,
                desiredRegionThreeId: createFunDayRequest.desiredRegionThreeId,
                goalOfFunDay: createFunDayRequest.goalOfFunDay,
                natureFunDayType: createFunDayRequest.natureFunDayType,
                natureFunDayComments: createFunDayRequest.natureFunDayComments,
                needBus: createFunDayRequest.needBus,
                needGuide: createFunDayRequest.needGuide,
                requestedFrom: createFunDayRequest.requestedFrom ?? null,
                requestedTo: createFunDayRequest.requestedTo ?? null,
                numberOfParticipants: createFunDayRequest.numberOfParticipants,
                visitors: createFunDayRequest.visitors,
                wantIncorporateLectureRecognition: createFunDayRequest.wantIncorporateLectureRecognition,
                incorporateLectureRecognitionBudget: createFunDayRequest.incorporateLectureRecognitionBudget,
                incorporateLectureRecognitionComments: createFunDayRequest.incorporateLectureRecognitionComments,
                mealType: createFunDayRequest.mealType,
                mealComments: createFunDayRequest.mealComments,
            };
            await api.createFunDayForm(request);
            await popupService.showSuccess(t("forms.CreateFunDay.AddedMsg"));
            navigate("/forms");
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            getCreateFunDayInitData();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="create-fun-day-container">
                <div className="create-fun-day-header">
                    {t("forms.CreateFunDay.Header")} (Organisa name/{formatDate(currentDate.toDateString())})
                </div>
                <div className="create-fun-day-content">
                    <div className="create-fun-day-content-up">
                        <BaseButton onClick={() => navigate("/forms")} type="button" className="basic-button">
                            <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                            <div>{t("forms.BackToFormsBtn")}</div>
                        </BaseButton>
                    </div>
                    <form onSubmit={submitOnClick}>
                        <div className="create-fun-day-content-middle">
                            <div className="create-fun-day-data-first">
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.PersonalInfo")}
                                    </div>
                                    <div className="fun-day-data-inputs-first">
                                        <div>
                                            <BaseInput
                                                required
                                                label={t("forms.CreateFunDay.ClientName")}
                                                type="search"
                                                size="small"
                                                value={createFunDayRequest.clientName}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        clientName: event.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                                            <div>
                                                <BaseInput
                                                    required
                                                    label={t("forms.CreateFunDay.Mobile")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.mobile}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            mobile: event.target.value,
                                                        }))
                                                    }
                                                    fullWidth
                                                />
                                            </div>
                                            <div>
                                                <BaseInput
                                                    required
                                                    label={t("forms.CreateFunDay.EmailAddress")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.email}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            email: event.target.value,
                                                        }))
                                                    }
                                                    fullWidth
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.FunDayInfo")}
                                    </div>
                                    <div className="fun-day-data-inputs-first">
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                                            <div>
                                                <BaseInput
                                                    required
                                                    label={t("forms.CreateFunDay.GoalOfFunDay")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.goalOfFunDay}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            goalOfFunDay: event.target.value,
                                                        }))
                                                    }
                                                    fullWidth
                                                />
                                            </div>
                                            <div>
                                                <BaseInput
                                                    required
                                                    label={t("forms.CreateFunDay.NumberParticipants")}
                                                    type="number"
                                                    size="small"
                                                    value={createFunDayRequest.numberOfParticipants}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            numberOfParticipants: Number(event.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                />
                                            </div>
                                        </div>
                                        <div
                                            style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}
                                        >
                                            <div>
                                                <BaseInput
                                                    required
                                                    select
                                                    label={t("forms.CreateFunDay.DesiredRegions")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.desiredRegionOneId}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            desiredRegionOneId: Number(event.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                >
                                                    {regions.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </BaseInput>
                                            </div>
                                            <div>
                                                <BaseInput
                                                    required
                                                    select
                                                    label={t("forms.CreateFunDay.DesiredRegions")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.desiredRegionTwoId}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            desiredRegionTwoId: Number(event.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                >
                                                    {regions.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </BaseInput>
                                            </div>
                                            <div>
                                                <BaseInput
                                                    required
                                                    select
                                                    label={t("forms.CreateFunDay.DesiredRegions")}
                                                    type="search"
                                                    size="small"
                                                    value={createFunDayRequest.desiredRegionThreeId}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            desiredRegionThreeId: Number(event.target.value),
                                                        }))
                                                    }
                                                    fullWidth
                                                >
                                                    {regions.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </BaseInput>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="create-fun-day-data-second">
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.Budget")}
                                    </div>
                                    <div className="fun-day-data-inputs-second">
                                        <div>
                                            <BaseInput
                                                required
                                                label={t("forms.CreateFunDay.BudgetEmployee")}
                                                type="search"
                                                size="small"
                                                value={createFunDayRequest.budgetPerEmployee}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        budgetPerEmployee: event.target.value,
                                                    }))
                                                }
                                                fullWidth
                                                InputProps={{
                                                    inputComponent: NumericFormatCustom as any,
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <BaseInput
                                                required
                                                label={t("forms.CreateFunDay.FamilyBudget")}
                                                type="search"
                                                size="small"
                                                value={createFunDayRequest.familyBudget}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        familyBudget: event.target.value,
                                                    }))
                                                }
                                                fullWidth
                                                InputProps={{
                                                    inputComponent: NumericFormatCustom as any,
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <BaseInput
                                                required
                                                label={t("forms.CreateFunDay.BudgetCouple")}
                                                type="search"
                                                fullWidth
                                                size="small"
                                                value={createFunDayRequest.budgetForCouple}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        budgetForCouple: event.target.value,
                                                    }))
                                                }
                                                InputProps={{
                                                    inputComponent: NumericFormatCustom as any,
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <BaseInput
                                                multiline
                                                maxRows={6}
                                                label={t("forms.CreateFunDay.Comments")}
                                                type="text"
                                                size="small"
                                                fullWidth
                                                sx={{
                                                    height: 166,
                                                }}
                                                inputProps={{
                                                    style: {
                                                        height: "100%",
                                                    },
                                                }}
                                                value={createFunDayRequest.budgetComments}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        budgetComments: event.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.SpecialRequest")}
                                    </div>
                                    <div className="fun-day-data-inputs-second">
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: "20px",
                                                width: "60%",
                                            }}
                                        >
                                            <div>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <TimePicker
                                                        ampm={false}
                                                        className="date-picker-input"
                                                        label={t("forms.CreateFunDay.RequestedFrom")}
                                                        renderInput={(params) => <BaseDateInput required {...params} />}
                                                        value={requestedFrom}
                                                        onChange={handleOnChangeRequestedFrom}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                            <div>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <TimePicker
                                                        ampm={false}
                                                        className="date-picker-input"
                                                        label={t("forms.CreateFunDay.RequestedTo")}
                                                        renderInput={(params) => <BaseDateInput required {...params} />}
                                                        value={requestedTo}
                                                        onChange={handleOnChangeRequestedTo}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 0.7fr 0.7fr",
                                                gap: "20px",
                                            }}
                                        >
                                            <div>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        className="date-picker-input"
                                                        label={t("forms.CreateFunDay.AlternativeHolidayDates")}
                                                        renderInput={(params) => <BaseDateInput required {...params} />}
                                                        value={createFunDayRequest.alternativeHolidayDateOne}
                                                        onChange={(event) => handleOnChangeDateOne(event)}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                            <div>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        className="date-picker-input"
                                                        renderInput={(params) => <BaseDateInput required {...params} />}
                                                        value={createFunDayRequest.alternativeHolidayDateTwo}
                                                        onChange={(event) => handleOnChangeDateTwo(event)}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                            <div>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        className="date-picker-input"
                                                        renderInput={(params) => <BaseDateInput {...params} />}
                                                        value={createFunDayRequest.alternativeHolidayDateThree}
                                                        onChange={(event) => handleOnChangeDateThree(event)}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                        </div>
                                        <div>
                                            <div>
                                                <BaseInput
                                                    multiline
                                                    maxRows={9}
                                                    label={t("forms.CreateFunDay.Comments")}
                                                    type="text"
                                                    size="small"
                                                    fullWidth
                                                    sx={{
                                                        height: 236,
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            height: "100%",
                                                        },
                                                    }}
                                                    value={createFunDayRequest.alternativeHolidayDateComments}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            alternativeHolidayDateComments: event.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ paddingBottom: "20px" }}>
                                        <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                            {t("forms.CreateFunDay.Visitors")}
                                        </div>
                                        <div className="fun-day-data-radio-btn">
                                            <RadioGroup defaultValue={VisitorsTypes.EmployeesOnly}>
                                                <FormControlLabel
                                                    value={VisitorsTypes.EmployeesOnly}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={(event: any) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            visitors: event.target.value,
                                                        }))
                                                    }
                                                    label={t("forms.CreateFunDay.EmployeesOnly")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                                <FormControlLabel
                                                    value={VisitorsTypes.IncludingCouples}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={(event: any) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            visitors: event.target.value,
                                                        }))
                                                    }
                                                    label={t("forms.CreateFunDay.IncludingCouples")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                                <FormControlLabel
                                                    value={VisitorsTypes.Families}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            color="default"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={(event: any) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            visitors: event.target.value,
                                                        }))
                                                    }
                                                    label={t("forms.CreateFunDay.Families")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                            </RadioGroup>
                                        </div>
                                    </div>
                                    <div style={{ paddingBottom: "20px" }}>
                                        <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                            {t("forms.CreateFunDay.NeedBus")}
                                        </div>
                                        <div className="fun-day-data-radio-btn">
                                            <RadioGroup defaultValue={true}>
                                                <FormControlLabel
                                                    value={true}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={() =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            needBus: true,
                                                        }))
                                                    }
                                                    label={t("forms.Yes")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                                <FormControlLabel
                                                    value={false}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={() =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            needBus: false,
                                                        }))
                                                    }
                                                    label={t("forms.No")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                            </RadioGroup>
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                            {t("forms.CreateFunDay.NeedGuide")}
                                        </div>
                                        <div className="fun-day-data-radio-btn">
                                            <RadioGroup defaultValue={true}>
                                                <FormControlLabel
                                                    value={true}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={() =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            needGuide: true,
                                                        }))
                                                    }
                                                    label={t("forms.Yes")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                                <FormControlLabel
                                                    value={false}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                "&, &.Mui-checked": {
                                                                    color: "#405289",
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    onChange={() =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            needGuide: false,
                                                        }))
                                                    }
                                                    label={t("forms.No")}
                                                    sx={{ height: "28.5px", marginRight: "-11px" }}
                                                    className="forms-radio-btn-text"
                                                />
                                            </RadioGroup>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 0.637fr", gap: "20px" }}>
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.NatureFunDay")}
                                    </div>
                                    <div
                                        style={{ display: "grid", gridTemplateColumns: "0.7fr 1fr", gap: "20px" }}
                                        className="fun-day-data-inputs-nature"
                                    >
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedChallenges}
                                                    onChange={handleCheckedChallenges}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.Challenges")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedTrip}
                                                    onChange={handleCheckedTrip}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.Trip")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedCombined}
                                                    onChange={handleCheckedCombined}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.Combined")}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <BaseInput
                                                multiline
                                                maxRows={3}
                                                label={t("forms.CreateFunDay.Comments")}
                                                type="text"
                                                size="small"
                                                fullWidth
                                                sx={{
                                                    height: 90,
                                                }}
                                                value={createFunDayRequest.natureFunDayComments}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        natureFunDayComments: event.target.value,
                                                    }))
                                                }
                                                inputProps={{
                                                    style: {
                                                        height: "100%",
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingBottom: "20px", paddingTop: "20px" }}
                                        className="font-style-18"
                                    >
                                        {t("forms.FunDay.WantToIncorporate")}
                                    </div>
                                    <div
                                        style={{ display: "grid", gridTemplateColumns: "0.35fr 1fr", gap: "15px" }}
                                        className="fun-day-data-inputs-third"
                                    >
                                        <div style={{ display: "grid", gap: "10px" }}>
                                            <div>
                                                <RadioGroup defaultValue={true}>
                                                    <FormControlLabel
                                                        value={true}
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    "&, &.Mui-checked": {
                                                                        color: "#405289",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        onChange={() =>
                                                            setCreateFunDayRequest((prevData) => ({
                                                                ...prevData,
                                                                wantIncorporateLectureRecognition: true,
                                                            }))
                                                        }
                                                        label={t("forms.Yes")}
                                                        sx={{ height: "28.5px", marginRight: "-11px" }}
                                                        className="forms-radio-btn-text"
                                                    />
                                                    <FormControlLabel
                                                        value={false}
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    "&, &.Mui-checked": {
                                                                        color: "#405289",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        onChange={() =>
                                                            setCreateFunDayRequest((prevData) => ({
                                                                ...prevData,
                                                                wantIncorporateLectureRecognition: false,
                                                            }))
                                                        }
                                                        label={t("forms.No")}
                                                        sx={{ height: "28.5px", marginRight: "-11px" }}
                                                        className="forms-radio-btn-text"
                                                    />
                                                </RadioGroup>
                                            </div>
                                            <div>
                                                <BaseInput
                                                    label={t("forms.CreateFunDay.Budget")}
                                                    type="search"
                                                    size="small"
                                                    InputProps={{
                                                        inputComponent: NumericFormatCustom as any,
                                                    }}
                                                    value={createFunDayRequest.incorporateLectureRecognitionBudget}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            incorporateLectureRecognitionBudget: event.target.value,
                                                        }))
                                                    }
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div>
                                                <BaseInput
                                                    multiline
                                                    maxRows={4}
                                                    label={t("forms.CreateFunDay.Comments")}
                                                    type="text"
                                                    size="small"
                                                    sx={{
                                                        width: "100%",
                                                        height: 107,
                                                    }}
                                                    inputProps={{
                                                        style: {
                                                            height: "100%",
                                                        },
                                                    }}
                                                    value={createFunDayRequest.incorporateLectureRecognitionComments}
                                                    onChange={(event) =>
                                                        setCreateFunDayRequest((prevData) => ({
                                                            ...prevData,
                                                            incorporateLectureRecognitionComments: event.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ paddingBottom: "20px" }} className="font-style-18">
                                        {t("forms.CreateFunDay.InterestedIn")}
                                    </div>
                                    <div className="fun-day-data-inputs-interested-in">
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedRefreshmentsArrival}
                                                    onChange={handleCheckedRefreshmentsOnArrival}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.RefreshmentsOnArrival")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedFullBreakfast}
                                                    onChange={handleCheckedFullBreakfast}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.FullBreakfast")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedRefreshmentsWay}
                                                    onChange={handleCheckedRefreshmentsWay}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.RefreshmentsOnTheWay")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedLunch}
                                                    onChange={handleCheckedLunch}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.Lunch")}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", height: "29.5px" }}>
                                                <Checkbox
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: "#2EA984",
                                                        },
                                                    }}
                                                    checked={isCheckedDinner}
                                                    onChange={handleCheckedDinner}
                                                />
                                                <div style={{ opacity: "0.7" }} className="font-style">
                                                    {t("forms.CreateFunDay.Dinner")}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ paddingTop: "20px", paddingInlineStart: "10px" }}>
                                            <BaseInput
                                                multiline
                                                maxRows={5}
                                                label={t("forms.CreateFunDay.Comments")}
                                                type="text"
                                                size="small"
                                                sx={{
                                                    width: "100%",
                                                    height: 132,
                                                }}
                                                value={createFunDayRequest.mealComments}
                                                onChange={(event) =>
                                                    setCreateFunDayRequest((prevData) => ({
                                                        ...prevData,
                                                        mealComments: event.target.value,
                                                    }))
                                                }
                                                inputProps={{
                                                    style: {
                                                        height: "100%",
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="create-fun-day-content-down">
                            <BaseButton
                                style={{ padding: "0px 63px" }}
                                onClick={() => navigate("/forms")}
                                className="cancel-button"
                                type="button"
                            >
                                {t("button.cancel")}
                            </BaseButton>
                            <BaseButton style={{ padding: "0px 63px" }} className="edit-save-button" type="submit">
                                {t("forms.SubmitBtn")}
                            </BaseButton>
                        </div>
                    </form>
                </div>
            </div>
        </CacheProvider>
    );
};

export default FormsCreateFunDay;
